import React from 'react';
import './table.css';
import Button from '../button/Button';
import TextField from '../text-field/TextField';
import Tooltip from '../tooltip/Tooltip';
import MuiTooltip from '@mui/material/Tooltip';
import { ITableColumnUI, ITableRowDataPoint } from './types';
import { toHtmlDate } from '../../utils/date';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';

export interface IRowFormProps {
	columns: ITableColumnUI[];
	values?: ITableRowDataPoint[];
	creating?: boolean;
	disabled?: boolean;
	onCancel: () => void;
	onSubmit: (values: { value: string | null; column: string }[]) => void;
	widths?: Record<string, number>;
}

function resolveTextFieldProps(type: string): {
	type: 'text' | 'number' | 'checkbox' | 'date';
	minlength?: number;
	maxlength?: number;
} {
	switch (type) {
		case 'number':
			return {
				type: 'number',
			};
		case 'boolean':
			return {
				type: 'checkbox',
			};
		case 'date':
			return {
				type: 'date',
			};
		case 'relation':
		case 'uuid':
			return {
				type: 'text',
				// Add min and max character length
				minlength: 36,
				maxlength: 36,
			};
		default:
			return {
				type: 'text',
			};
	}
}

function getColumnDefaultValue(type: string): string {
	switch (type) {
		case 'boolean':
			return 'false';
		case 'number':
			return '0';
		default:
			return '';
	}
}

function formatValueForInput(val: string | null, type: string): string {
	if (val === null) return '';
	if (type === 'date' && val) {
		try {
			return toHtmlDate(new Date(val));
		} catch (e) {
			return '';
		}
	}
	return val;
}

export default function RowForm(props: IRowFormProps) {
	const { columns = [], values = [], disabled, widths } = props;
	const rowFormId = props.creating
		? 'table-new-row-form'
		: 'table-edit-row-form';

	const [formValues, setFormValues] = React.useState<Record<string, string | null>>(() => {
		const initial: Record<string, string | null> = {};
		columns.forEach((column, index) => {
			if (props.creating) {
				initial[column.value] = column.nullable ? null : getColumnDefaultValue(column.type);
			} else {
				initial[column.value] = values[index]?.value !== undefined ? values[index].value : (column.nullable ? null : getColumnDefaultValue(column.type));
			}
		});
		return initial;
	});

	return (
		<tr className='ds_table-row'>
			<form
				id={rowFormId}
				style={{ display: 'none' }}
				onSubmit={(e) => {
					e.preventDefault();
					const mergedValues = columns.map((column, index) => {
						const isEditable = column.editable || props.creating;
						const val = formValues[column.value];
						let finalValue = isEditable ? val : (values[index]?.value !== undefined ? values[index].value : null);

						if (column.type === 'number') {
							if (finalValue === null || finalValue === '') {
								finalValue = column.nullable ? null : '0';
							} else {
								const num = Number(finalValue);
								finalValue = isNaN(num) ? '0' : String(num);
							}
						}

						return {
							column: column.value,
							value: finalValue,
						};
					});
					props.onSubmit(mergedValues);
				}}
			></form>
			<span className='ds_table-row-cell-group'>
				<td className='ds_table-row-cell ds_table-head-cell-checkbox ds_table-column__size-xs'>
					{/* Placeholder column, left empty on purpose */}
				</td>
				{columns.map((column, index) => {
					const { type, ...inputProps } = resolveTextFieldProps(
						column.type || 'text'
					);

					const isEditable = column.editable || props.creating;
					const currentValue = formValues[column.value];
					const isUUID = column.type === 'uuid';
					const showUUIDGenerator = isUUID && isEditable;

					let cellContent: React.ReactNode = null;

					if (!isEditable) {
						cellContent = (
							<span style={{ color: 'var(--color-main-grey)' }}>
								{currentValue !== null && currentValue !== undefined ? currentValue : '—'}
							</span>
						);
					} else if (currentValue === null) {
						cellContent = (
							<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
								<span style={{ color: 'var(--color-light-grey)' }}>—</span>
								<MuiTooltip title="Initialize field" placement="top">
									<button
										type='button'
										disabled={disabled}
										onClick={() => {
											setFormValues((prev) => ({
												...prev,
												[column.value]: getColumnDefaultValue(column.type),
											}));
										}}
										style={{
											width: '20px',
											height: '20px',
											borderRadius: '50%',
											border: 'none',
											backgroundColor: 'var(--color-main-primary)',
											color: 'white',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											cursor: 'pointer',
											fontSize: '10px',
											padding: 0,
										}}
									>
										<i className='fa-solid fa-plus' />
									</button>
								</MuiTooltip>
							</div>
						);
					} else {
						let inputField: React.ReactNode = null;
						if (type === 'checkbox') {
							inputField = (
								<input
									type='checkbox'
									form={rowFormId}
									name={column.value}
									disabled={disabled}
									checked={currentValue === 'true'}
									onChange={(e) => {
										setFormValues((prev) => ({
											...prev,
											[column.value]: e.target.checked ? 'true' : 'false',
										}));
									}}
								/>
							);
						} else if (type === 'date') {
							inputField = (
								<DateTimePicker
									disabled={disabled}
									value={currentValue ? dayjs(currentValue) : null}
									onChange={(newValue) => {
										setFormValues((prev) => ({
											...prev,
											[column.value]: newValue ? newValue.toISOString() : null,
										}));
									}}
									label={column.label}
									name={column.value}
									slotProps={{
										textField: {
											variant: 'filled',
											required: !column.nullable,
											fullWidth: true,
											InputLabelProps: {
												sx: {
													maxWidth: 'calc(100% - 40px)',
													textOverflow: 'ellipsis',
													whiteSpace: 'nowrap',
													overflow: 'hidden'
												}
											}
										}
									}}
								/>
							);
						} else {
							inputField = (
								<TextField
									form={rowFormId}
									disabled={disabled}
									value={formatValueForInput(currentValue, column.type)}
									onChange={(val: string) => {
										setFormValues((prev) => ({
											...prev,
											[column.value]: val,
										}));
									}}
									label={column.label}
									variant='filled'
									name={column.value}
									type={type as 'text' | 'number'}
									{...inputProps}
								/>
							);
						}

						if (showUUIDGenerator) {
							inputField = (
								<div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
									<div style={{ flexGrow: 1, minWidth: 0 }}>
										{inputField}
									</div>
									<MuiTooltip title="Generate UUID" placement="top">
										<button
											type='button'
											disabled={disabled}
											onClick={() => {
												setFormValues((prev) => ({
													...prev,
													[column.value]: crypto.randomUUID(),
												}));
											}}
											style={{
												width: '20px',
												height: '20px',
												borderRadius: '50%',
												border: 'none',
												backgroundColor: 'var(--color-main-primary)',
												color: 'white',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												cursor: 'pointer',
												fontSize: '10px',
												padding: 0,
												flexShrink: 0,
											}}
										>
											<i className='fa-solid fa-wand-magic-sparkles' />
										</button>
									</MuiTooltip>
								</div>
							);
						}

						if (column.nullable) {
							cellContent = (
								<div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
									<div style={{ flexGrow: 1, minWidth: 0 }}>
										{inputField}
									</div>
									<MuiTooltip title="Set to null" placement="top">
										<button
											type='button'
											disabled={disabled}
											onClick={() => {
												setFormValues((prev) => ({
													...prev,
													[column.value]: null,
												}));
											}}
											style={{
												width: '20px',
												height: '20px',
												borderRadius: '50%',
												border: 'none',
												backgroundColor: 'var(--color-main-error)',
												color: 'white',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												cursor: 'pointer',
												fontSize: '10px',
												padding: 0,
												flexShrink: 0,
											}}
										>
											<i className='fa-solid fa-xmark' />
										</button>
									</MuiTooltip>
								</div>
							);
						} else {
							cellContent = inputField;
						}
					}

					const width = widths?.[column.value];
					const style = width ? { width: `${width}px`, flexShrink: 0 } : undefined;

					return (
						<td
							key={column.value}
							className={`ds_table-row-cell ds_table-column__size-${columns[index].size}`}
							style={style}
						>
							{cellContent}
						</td>
					);
				})}
			</span>
			<td className='ds_table-row-cell ds_table-column__size-s'>
				<Button
					form={rowFormId}
					iconStart='fa-solid fa-xmark'
					disabled={disabled}
					size='small'
					color='error'
					type='button'
					id='table-row-actions-menu-new-row-cancel-button'
					onClick={() => {
						props.onCancel?.();
					}}
				/>
				<Button
					form={rowFormId}
					iconStart='fa-solid fa-check'
					disabled={disabled}
					size='small'
					type='submit'
					id='table-row-actions-menu-new-row-save-button'
				/>
				<Tooltip
					placement='bottom'
					anchorQuerySelector='#table-row-actions-menu-new-row-cancel-button'
				>
					Cancel
				</Tooltip>
				<Tooltip
					placement='bottom'
					anchorQuerySelector='#table-row-actions-menu-new-row-save-button'
				>
					Save row
				</Tooltip>
			</td>
		</tr>
	);
}

